import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { pagamentosAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Skeleton } from './ui/skeleton';
import { Badge } from './ui/badge';
import { DollarSign, Calendar, ChevronRight } from 'lucide-react';

const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const formatBR = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString('pt-BR');
  } catch {
    return iso;
  }
};

const formatCurrency = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const targetForProj = (statusProjeto, projetoId) =>
  statusProjeto === 'arquivado'
    ? `/projetos/arquivados?project=${projetoId}`
    : `/projetos?project=${projetoId}`;

/**
 * Seção "Pagamentos do Mês" para Relatórios — Fase 4.
 * Mostra projetos com parcelas no mês + agenda linear de parcelas + totais pago/pendente.
 */
const PagamentosMonthSection = () => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pagamentosAPI.byMonth(year, month);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const anos = [];
  for (let y = today.getFullYear() - 2; y <= today.getFullYear() + 2; y++) anos.push(y);

  return (
    <Card data-testid="pagamentos-month-section">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-500" />
              Pagamentos do Mês
            </CardTitle>
            <CardDescription>
              Parcelas de serviço com vencimento em {MESES[month - 1]}/{year}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="w-36" data-testid="pag-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MESES.map((m, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-24" data-testid="pag-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {anos.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : !data || data.total_parcelas === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Calendar className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Nenhuma parcela com vencimento neste mês.</p>
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg border bg-muted/30">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Parcelas</p>
                <p className="text-lg font-bold">{data.total_parcelas}</p>
              </div>
              <div className="p-3 rounded-lg border bg-muted/30">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Projetos</p>
                <p className="text-lg font-bold">{data.total_projetos}</p>
              </div>
              <div className="p-3 rounded-lg border bg-emerald-500/5 border-emerald-500/20">
                <p className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400">Recebido</p>
                <p className="text-lg font-bold text-emerald-700 dark:text-emerald-400">{formatCurrency(data.total_pago)}</p>
              </div>
              <div className="p-3 rounded-lg border bg-amber-500/5 border-amber-500/20">
                <p className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-400">A Receber</p>
                <p className="text-lg font-bold text-amber-700 dark:text-amber-400">{formatCurrency(data.total_pendente)}</p>
              </div>
            </div>

            {/* Agenda linear */}
            <div>
              <h4 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                Agenda — 1 linha por parcela
              </h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vencimento</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Projeto</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.parcelas.map((p) => (
                    <TableRow key={p.parcela_id} data-testid={`parc-row-${p.parcela_id}`}>
                      <TableCell className="font-mono text-sm whitespace-nowrap">{formatBR(p.data_vencimento)}</TableCell>
                      <TableCell className="font-medium">{p.cliente_nome}</TableCell>
                      <TableCell className="font-semibold whitespace-nowrap">{formatCurrency(p.valor)}</TableCell>
                      <TableCell>
                        {p.pago ? (
                          <Badge className="bg-emerald-500/10 text-emerald-700 border-emerald-500/30">Paga</Badge>
                        ) : (
                          <Badge className="bg-amber-500/10 text-amber-700 border-amber-500/30">Pendente</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {p.tipo_projeto}
                        {p.numero_interno && <span className="text-muted-foreground"> · {p.numero_interno}</span>}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(targetForProj(p.status_projeto, p.projeto_id))}
                          data-testid={`parc-goto-${p.parcela_id}`}
                        >
                          Ver <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Projetos únicos */}
            <div>
              <h4 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                Projetos com parcelas no mês
              </h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Nº Interno</TableHead>
                    <TableHead>Parcelas</TableHead>
                    <TableHead>Pago</TableHead>
                    <TableHead>Pendente</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.projetos.map((p) => (
                    <TableRow key={p.id} data-testid={`parc-proj-${p.id}`}>
                      <TableCell className="font-medium">{p.cliente_nome}</TableCell>
                      <TableCell>{p.tipo_projeto || '-'}</TableCell>
                      <TableCell className="font-mono text-xs">{p.numero_interno || '-'}</TableCell>
                      <TableCell>{p.qtd_parcelas_mes}</TableCell>
                      <TableCell className="text-emerald-600 font-semibold">{formatCurrency(p.total_pago_mes)}</TableCell>
                      <TableCell className="text-amber-600 font-semibold">{formatCurrency(p.total_pendente_mes)}</TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(targetForProj(p.status, p.id))}
                          data-testid={`parc-goto-proj-${p.id}`}
                        >
                          Abrir <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PagamentosMonthSection;
